/**
 * Workspaces Tab Component
 * 
 * Tab content for managing workspaces within the Settings page.
 * Combines workspace list, creation, and editing functionality.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useWorkspaceStore } from '../../store/workspaceStore';
import { useAuthStore } from '../../store/authStore';
import { workspaceService } from '../../services/workspace.service';
import {
  WorkspaceType,
  WorkspaceWithRole,
  CreateWorkspaceRequest,
  UpdateWorkspaceRequest,
  WORKSPACE_TYPE_LABELS,
  WORKSPACE_ROLE_LABELS,
} from '@budgira/shared';
import { cn } from '../../utils/cn';
import { Card } from '../ui';
import { EmojiPicker } from '../common/EmojiPicker';

// Workspace type icons/emojis
const WORKSPACE_TYPE_ICONS: Record<WorkspaceType, string> = {
  personal: '👤',
  business: '💼',
  association: '🤝',
  other: '📁',
};

const WORKSPACE_TYPES: WorkspaceType[] = ['personal', 'business', 'association', 'other'];

type ViewMode = 'list' | 'create' | 'edit' | 'rename';

interface WorkspacesTabProps {
  initialWorkspaceId?: string | null;
}

export const WorkspacesTab = ({ initialWorkspaceId }: WorkspacesTabProps) => {
  const { t, i18n } = useTranslation();
  const language = i18n.language as 'fr' | 'en';

  const {
    workspaces,
    currentWorkspace,
    pendingInvitations,
    fetchWorkspaces,
    fetchPendingInvitations,
    switchWorkspace,
    createWorkspace,
    updateWorkspace,
    deleteWorkspace,
    acceptInvitation,
    isLoading,
    error,
    clearError,
  } = useWorkspaceStore();

  const { isPremium } = useAuthStore();

  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [editingWorkspace, setEditingWorkspace] = useState<WorkspaceWithRole | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Rename state (for non-owners to set display name)
  const [renameDisplayName, setRenameDisplayName] = useState('');
  const [renameError, setRenameError] = useState<string | null>(null);
  const [isRenaming, setIsRenaming] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'personal' as WorkspaceType,
    logoUrl: '',
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  // Load data on mount
  useEffect(() => {
    fetchWorkspaces();
    fetchPendingInvitations();
  }, [fetchWorkspaces, fetchPendingInvitations]);

  // Handle initial workspace edit from URL
  useEffect(() => {
    if (initialWorkspaceId && workspaces.length > 0) {
      const workspace = workspaces.find((w) => w.id === initialWorkspaceId);
      if (workspace && (workspace.role === 'owner' || workspace.role === 'admin')) {
        handleEdit(workspace);
      }
    }
  }, [initialWorkspaceId, workspaces]);

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      type: 'personal',
      logoUrl: '',
    });
    setFormErrors({});
    setShowEmojiPicker(false);
    clearError();
  };

  const handleCreate = () => {
    resetForm();
    setEditingWorkspace(null);
    setViewMode('create');
  };

  const handleEdit = (workspace: WorkspaceWithRole) => {
    setFormData({
      name: workspace.name,
      description: workspace.description || '',
      type: workspace.type,
      logoUrl: workspace.logoUrl || '',
    });
    setFormErrors({});
    setEditingWorkspace(workspace);
    setViewMode('edit');
    clearError();
  };

  const handleCancel = () => {
    resetForm();
    setEditingWorkspace(null);
    setRenameDisplayName('');
    setRenameError(null);
    setViewMode('list');
  };

  // Handle rename (display name) for non-owner members
  const handleRename = (workspace: WorkspaceWithRole) => {
    setEditingWorkspace(workspace);
    setRenameDisplayName(workspace.displayName || workspace.name);
    setRenameError(null);
    setViewMode('rename');
  };

  const handleSubmitRename = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingWorkspace) return;

    const trimmedName = renameDisplayName.trim();
    if (trimmedName.length > 100) {
      setRenameError(t('workspaces.form.errors.nameTooLong'));
      return;
    }

    setIsRenaming(true);
    setRenameError(null);

    try {
      // If the name matches the original workspace name, reset to null
      const displayNameToSet = trimmedName === editingWorkspace.name ? null : trimmedName || null;
      
      const response = await workspaceService.updateDisplayName(editingWorkspace.id, displayNameToSet);
      if (response.success) {
        // Refresh workspaces to get the updated display name
        await fetchWorkspaces();
        handleCancel();
      } else {
        setRenameError(response.error?.message || t('common.error'));
      }
    } catch (err) {
      setRenameError(t('common.error'));
    } finally {
      setIsRenaming(false);
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.name.trim()) {
      errors.name = t('workspaces.form.errors.nameRequired');
    } else if (formData.name.length > 50) {
      errors.name = t('workspaces.form.errors.nameTooLong');
    }

    if (formData.description && formData.description.length > 200) {
      errors.description = t('workspaces.form.errors.descriptionTooLong');
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    const data: CreateWorkspaceRequest | UpdateWorkspaceRequest = {
      name: formData.name.trim(),
      description: formData.description.trim() || undefined,
      type: formData.type,
      logoUrl: formData.logoUrl.trim() || undefined,
    };

    setActionLoading('submit');

    let success: boolean;
    if (viewMode === 'edit' && editingWorkspace) {
      const result = await updateWorkspace(editingWorkspace.id, data);
      success = result !== null;
    } else {
      const result = await createWorkspace(data as CreateWorkspaceRequest);
      success = result !== null;
    }

    setActionLoading(null);

    if (success) {
      handleCancel();
    }
  };

  const handleSwitch = async (workspaceId: string) => {
    if (workspaceId === currentWorkspace?.id) return;

    setActionLoading(workspaceId);
    const success = await switchWorkspace(workspaceId);
    setActionLoading(null);

    if (success) {
      window.location.href = '/dashboard';
    }
  };

  const handleDelete = async (workspaceId: string) => {
    setActionLoading(workspaceId);
    const success = await deleteWorkspace(workspaceId);
    setActionLoading(null);
    setDeleteConfirmId(null);

    if (success && workspaceId === currentWorkspace?.id) {
      window.location.href = '/dashboard';
    }
  };

  const handleAcceptInvitation = async (token: string) => {
    setActionLoading(token);
    const workspace = await acceptInvitation(token);
    setActionLoading(null);

    if (workspace) {
      await switchWorkspace(workspace.id);
      window.location.href = '/dashboard';
    }
  };

  const getWorkspaceIcon = (workspace: WorkspaceWithRole) => {
    if (workspace.logoUrl) {
      if (workspace.logoUrl.length <= 4 && /\p{Emoji}/u.test(workspace.logoUrl)) {
        return workspace.logoUrl;
      }
      return null;
    }
    return WORKSPACE_TYPE_ICONS[workspace.type];
  };

  const getTypeLabel = (type: WorkspaceType) => WORKSPACE_TYPE_LABELS[type][language];
  const getRoleLabel = (role: string) =>
    WORKSPACE_ROLE_LABELS[role as keyof typeof WORKSPACE_ROLE_LABELS]?.[language] || role;

  const canCreateWorkspace = isPremium() || workspaces.length < 1;
  const maxWorkspaces = isPremium() ? 10 : 1;

  // Render rename form (for non-owners to customize display name)
  if (viewMode === 'rename' && editingWorkspace) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
              {t('workspaces.rename', 'Renommer l\'espace')}
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {t('workspaces.renameDescription', 'Personnalisez le nom de cet espace tel qu\'il apparaît pour vous. Ce changement n\'affecte pas les autres membres.')}
            </p>
          </div>
          <button
            onClick={handleCancel}
            className="text-sm text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
          >
            {t('common.cancel')}
          </button>
        </div>

        {/* Info about original name */}
        <div className="p-4 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg">
          <p className="text-sm text-blue-700 dark:text-blue-300">
            {t('workspaces.originalNameInfo', 'Nom original défini par le propriétaire : {{name}}', { name: editingWorkspace.name })}
          </p>
        </div>

        {/* Error display */}
        {renameError && (
          <div className="p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-sm text-red-700 dark:text-red-300">{renameError}</p>
          </div>
        )}

        {/* Simple form */}
        <form onSubmit={handleSubmitRename} className="space-y-6">
          <Card className="p-6 space-y-6">
            <div>
              <label
                htmlFor="display-name"
                className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
              >
                {t('workspaces.displayName', 'Nom personnalisé')}
              </label>
              <input
                type="text"
                id="display-name"
                value={renameDisplayName}
                onChange={(e) => setRenameDisplayName(e.target.value)}
                placeholder={editingWorkspace.name}
                className={cn(
                  'w-full px-4 py-2 border rounded-lg transition-colors',
                  'bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100',
                  'border-slate-300 dark:border-slate-600 focus:ring-violet-500 focus:border-violet-500'
                )}
                maxLength={100}
                autoFocus
              />
              <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">
                {t('workspaces.displayNameHint', 'Laissez vide pour utiliser le nom original')}
              </p>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t border-slate-200 dark:border-slate-700">
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 rounded-lg transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                disabled={isRenaming}
                className="px-4 py-2 text-sm font-medium text-white bg-violet-600 hover:bg-violet-700 disabled:bg-violet-300 rounded-lg transition-colors flex items-center gap-2"
              >
                {isRenaming && <LoadingSpinner className="w-4 h-4" />}
                {t('common.save')}
              </button>
            </div>
          </Card>
        </form>
      </div>
    );
  }

  // Render form (create or edit)
  if (viewMode === 'create' || viewMode === 'edit') {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
              {viewMode === 'edit'
                ? t('workspaces.form.editTitle')
                : t('workspaces.form.createTitle')}
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {viewMode === 'edit'
                ? t('workspaces.form.editSubtitle')
                : t('workspaces.form.createSubtitle')}
            </p>
          </div>
          <button
            onClick={handleCancel}
            className="text-sm text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
          >
            {t('common.cancel')}
          </button>
        </div>

        {/* Error display */}
        {error && (
          <div className="p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <Card className="p-6 space-y-6">
            {/* Name */}
            <div>
              <label
                htmlFor="workspace-name"
                className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
              >
                {t('workspaces.form.name')} *
              </label>
              <input
                type="text"
                id="workspace-name"
                value={formData.name}
                onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                placeholder={t('workspaces.form.namePlaceholder')}
                className={cn(
                  'w-full px-4 py-2 border rounded-lg transition-colors',
                  'bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100',
                  formErrors.name
                    ? 'border-red-300 dark:border-red-600 focus:ring-red-500'
                    : 'border-slate-300 dark:border-slate-600 focus:ring-violet-500 focus:border-violet-500'
                )}
                maxLength={50}
              />
              {formErrors.name && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{formErrors.name}</p>
              )}
            </div>

            {/* Type */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                {t('workspaces.form.type')} *
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {WORKSPACE_TYPES.map((workspaceType) => (
                  <button
                    key={workspaceType}
                    type="button"
                    onClick={() => setFormData((prev) => ({ ...prev, type: workspaceType }))}
                    className={cn(
                      'flex flex-col items-center p-4 rounded-lg border-2 transition-all',
                      formData.type === workspaceType
                        ? 'border-violet-500 bg-violet-50 dark:bg-violet-900/30'
                        : 'border-slate-200 dark:border-slate-600 hover:border-slate-300 dark:hover:border-slate-500'
                    )}
                  >
                    <span className="text-2xl mb-1">{WORKSPACE_TYPE_ICONS[workspaceType]}</span>
                    <span
                      className={cn(
                        'text-sm font-medium',
                        formData.type === workspaceType
                          ? 'text-violet-700 dark:text-violet-300'
                          : 'text-slate-700 dark:text-slate-300'
                      )}
                    >
                      {getTypeLabel(workspaceType)}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Description */}
            <div>
              <label
                htmlFor="workspace-description"
                className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
              >
                {t('workspaces.form.description')}
              </label>
              <textarea
                id="workspace-description"
                value={formData.description}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, description: e.target.value }))
                }
                placeholder={t('workspaces.form.descriptionPlaceholder')}
                rows={3}
                className={cn(
                  'w-full px-4 py-2 border rounded-lg transition-colors resize-none',
                  'bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100',
                  formErrors.description
                    ? 'border-red-300 dark:border-red-600 focus:ring-red-500'
                    : 'border-slate-300 dark:border-slate-600 focus:ring-violet-500 focus:border-violet-500'
                )}
                maxLength={200}
              />
              <div className="mt-1 flex justify-between">
                {formErrors.description && (
                  <p className="text-sm text-red-600 dark:text-red-400">
                    {formErrors.description}
                  </p>
                )}
                <p className="text-xs text-slate-400 dark:text-slate-500 ml-auto">
                  {formData.description.length}/200
                </p>
              </div>
            </div>

            {/* Icon/Emoji */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                {t('workspaces.form.logo')}
              </label>
              <div className="flex items-center gap-4 mb-3">
                <div className="w-16 h-16 flex items-center justify-center bg-slate-100 dark:bg-slate-700 rounded-xl">
                  <span className="text-4xl">
                    {formData.logoUrl && /\p{Emoji}/u.test(formData.logoUrl)
                      ? formData.logoUrl
                      : WORKSPACE_TYPE_ICONS[formData.type]}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                  className="px-4 py-2 text-sm font-medium text-violet-700 dark:text-violet-300 bg-violet-50 dark:bg-violet-900/30 hover:bg-violet-100 dark:hover:bg-violet-900/50 rounded-lg transition-colors"
                >
                  {showEmojiPicker
                    ? t('emojiPicker.close', 'Fermer')
                    : t('workspaces.form.chooseIcon', 'Choisir une icône')}
                </button>
              </div>
              {showEmojiPicker && (
                <EmojiPicker
                  value={formData.logoUrl}
                  onChange={(emoji) => {
                    setFormData((prev) => ({ ...prev, logoUrl: emoji }));
                  }}
                  className="mb-4"
                />
              )}
              <p className="text-xs text-slate-400 dark:text-slate-500">
                {t('workspaces.form.logoHelpEmoji', "Choisissez un emoji pour personnaliser votre espace")}
              </p>
            </div>
          </Card>

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={handleCancel}
              className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={actionLoading === 'submit'}
              className="px-4 py-2 text-sm font-medium text-white bg-violet-600 hover:bg-violet-700 disabled:bg-violet-300 rounded-lg transition-colors min-w-[100px]"
            >
              {actionLoading === 'submit' ? (
                <LoadingSpinner className="w-5 h-5 mx-auto" />
              ) : viewMode === 'edit' ? (
                t('common.save')
              ) : (
                t('workspaces.form.create')
              )}
            </button>
          </div>
        </form>
      </div>
    );
  }

  // Render list view
  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div>
          <h2 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-slate-100">
            {t('workspaces.title')}
          </h2>
          <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">
            {t('workspaces.subtitle')}
          </p>
        </div>

        {canCreateWorkspace && (
          <button
            onClick={handleCreate}
            className="inline-flex items-center justify-center px-3 sm:px-4 py-2 text-sm font-medium text-white bg-violet-600 hover:bg-violet-700 rounded-lg transition-colors min-h-[40px] sm:min-h-[44px]"
          >
            <PlusIcon className="w-4 h-4 sm:w-5 sm:h-5 mr-1.5 sm:mr-2" />
            {t('workspaces.create')}
          </button>
        )}
      </div>

      {/* Error display */}
      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
        </div>
      )}

      {/* Pending invitations */}
      {pendingInvitations.length > 0 && (
        <div className="bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-xl p-4">
          <h3 className="text-base font-medium text-amber-800 dark:text-amber-200 mb-3">
            {t('workspaces.pendingInvitations')}
          </h3>
          <div className="space-y-3">
            {pendingInvitations.map((invitation) => (
              <div
                key={invitation.id}
                className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 bg-white dark:bg-slate-800 rounded-lg"
              >
                <div>
                  <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                    {invitation.workspaceName}
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {t('workspaces.invitedAs', { role: getRoleLabel(invitation.role) })}
                  </p>
                </div>
                <button
                  onClick={() => handleAcceptInvitation(invitation.token)}
                  disabled={actionLoading === invitation.token}
                  className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 disabled:bg-amber-300 rounded-lg transition-colors min-h-[40px]"
                >
                  {actionLoading === invitation.token ? (
                    <LoadingSpinner className="w-4 h-4" />
                  ) : (
                    t('workspaces.acceptInvitation')
                  )}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Usage info */}
      <div className="text-sm text-slate-500 dark:text-slate-400">
        {t('workspaces.usage', { count: workspaces.length, max: maxWorkspaces })}
        {!isPremium() && workspaces.length >= 1 && (
          <span className="ml-2">
            <a href="/premium" className="text-violet-600 dark:text-violet-400 hover:underline">
              {t('workspaces.upgradeToPremium')}
            </a>
          </span>
        )}
      </div>

      {/* Loading state */}
      {isLoading && workspaces.length === 0 && (
        <div className="text-center py-12">
          <LoadingSpinner className="w-8 h-8 mx-auto text-violet-600" />
          <p className="mt-2 text-slate-500 dark:text-slate-400">{t('common.loading')}</p>
        </div>
      )}

      {/* Workspace list */}
      {!isLoading && workspaces.length === 0 ? (
        <Card className="p-6 sm:p-12 text-center">
          <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center">
            <FolderIcon className="w-6 h-6 sm:w-8 sm:h-8 text-slate-400" />
          </div>
          <h3 className="text-base sm:text-lg font-medium text-slate-900 dark:text-slate-100 mb-2">
            {t('workspaces.empty.title')}
          </h3>
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
            {t('workspaces.empty.message')}
          </p>
          {canCreateWorkspace && (
            <button
              onClick={handleCreate}
              className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-violet-600 hover:bg-violet-700 rounded-lg transition-colors"
            >
              <PlusIcon className="w-5 h-5 mr-2" />
              {t('workspaces.createFirst')}
            </button>
          )}
        </Card>
      ) : (
        <div className="space-y-2 sm:space-y-3">
          {workspaces.map((workspace) => (
            <Card
              key={workspace.id}
              className={cn(
                'p-3 sm:p-4 transition-all',
                workspace.id === currentWorkspace?.id &&
                  'ring-2 ring-violet-200 dark:ring-violet-800'
              )}
            >
              {/* Mobile layout: more compact */}
              <div className="flex items-start gap-2.5 sm:gap-4">
                {/* Icon */}
                <div className="w-9 h-9 sm:w-12 sm:h-12 flex-shrink-0 flex items-center justify-center bg-slate-100 dark:bg-slate-700 rounded-lg">
                  <span className="text-lg sm:text-2xl">{getWorkspaceIcon(workspace)}</span>
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
                    <h4 className="text-sm sm:text-base font-medium text-slate-900 dark:text-slate-100">
                      {workspace.displayName || workspace.name}
                    </h4>
                    {/* Show original name if user has custom display name */}
                    {workspace.displayName && workspace.displayName !== workspace.name && (
                      <span className="text-xs text-slate-400 dark:text-slate-500" title={t('workspaces.originalName', 'Nom original')}>
                        ({workspace.name})
                      </span>
                    )}
                    {workspace.id === currentWorkspace?.id && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] sm:text-xs font-medium bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300">
                        {t('workspaces.active')}
                      </span>
                    )}
                    {workspace.isDefault && workspace.role === 'owner' && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] sm:text-xs font-medium bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300">
                        {t('workspaces.default')}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1 sm:gap-2 mt-0.5 sm:mt-1">
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] sm:text-xs font-medium bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300">
                      {getTypeLabel(workspace.type)}
                    </span>
                    <span
                      className={cn(
                        'inline-flex items-center px-1.5 py-0.5 rounded text-[10px] sm:text-xs font-medium',
                        workspace.role === 'owner'
                          ? 'bg-violet-100 dark:bg-violet-900/50 text-violet-700 dark:text-violet-300'
                          : workspace.role === 'admin'
                          ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300'
                          : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300'
                      )}
                    >
                      {getRoleLabel(workspace.role)}
                    </span>
                  </div>
                  {workspace.description && (
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 line-clamp-1">
                      {workspace.description}
                    </p>
                  )}
                </div>

                {/* Actions - inline */}
                <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                  {workspace.id !== currentWorkspace?.id && (
                    <button
                      onClick={() => handleSwitch(workspace.id)}
                      disabled={actionLoading === workspace.id}
                      className="inline-flex items-center justify-center px-2 sm:px-3 py-1 sm:py-1.5 text-xs font-medium text-violet-700 dark:text-violet-300 bg-violet-50 dark:bg-violet-900/30 hover:bg-violet-100 dark:hover:bg-violet-900/50 rounded-lg transition-colors"
                    >
                      {actionLoading === workspace.id ? (
                        <LoadingSpinner className="w-3 h-3 sm:w-4 sm:h-4" />
                      ) : (
                        t('workspaces.switch')
                      )}
                    </button>
                  )}

                  {(workspace.role === 'owner' || workspace.role === 'admin') && (
                    <button
                      onClick={() => handleEdit(workspace)}
                      className="inline-flex items-center justify-center p-1.5 sm:p-2 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                      title={t('common.edit')}
                    >
                      <EditIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                    </button>
                  )}

                  {/* Rename button for non-owner members */}
                  {workspace.role !== 'owner' && workspace.role !== 'admin' && (
                    <button
                      onClick={() => handleRename(workspace)}
                      className="inline-flex items-center justify-center p-1.5 sm:p-2 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                      title={t('workspaces.rename', 'Renommer')}
                    >
                      <EditIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                    </button>
                  )}

                  {workspace.role === 'owner' && !workspace.isDefault && (
                    deleteConfirmId === workspace.id ? (
                      <div className="flex gap-1">
                        <button
                          onClick={() => handleDelete(workspace.id)}
                          disabled={actionLoading === workspace.id}
                          className="inline-flex items-center justify-center px-2 py-1 text-xs font-medium text-white bg-red-600 hover:bg-red-700 disabled:bg-red-300 rounded-lg transition-colors"
                        >
                          {actionLoading === workspace.id ? (
                            <LoadingSpinner className="w-3 h-3" />
                          ) : (
                            t('common.confirm')
                          )}
                        </button>
                        <button
                          onClick={() => setDeleteConfirmId(null)}
                          className="inline-flex items-center justify-center px-2 py-1 text-xs font-medium text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 rounded-lg transition-colors"
                        >
                          {t('common.cancel')}
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setDeleteConfirmId(workspace.id)}
                        className="inline-flex items-center justify-center p-1.5 sm:p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                        title={t('common.delete')}
                      >
                        <TrashIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                      </button>
                    )
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// Icons
const PlusIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
  </svg>
);

const FolderIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
    />
  </svg>
);

const EditIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
    />
  </svg>
);

const TrashIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
    />
  </svg>
);

const LoadingSpinner = ({ className }: { className?: string }) => (
  <svg className={cn('animate-spin', className)} fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
  </svg>
);

export default WorkspacesTab;
